/**
 * Pure helpers for the wizard's Step 3 (D1.5.4) brand-kit form. Mirrors
 * the persisted shape that `packages/config/src/brand-kit.ts` parses at
 * commit time, so the draft round-trips through Zod without further
 * shape-massaging at the commit boundary.
 *
 * The form is intentionally a thin layer on top of the canonical schema:
 * required slots only, no optional fields (dark-mode logo, secondary
 * colors, terminology repeater) — those land with the intake portal
 * (D2.2.4) where the SMB does the rich self-serve. The wizard is the
 * platform-admin path for a kit that's already been agreed in writing.
 */
import type { BrandAssetKind } from './api.js';

export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export type HexValidationError =
  | 'empty'
  | 'missing_hash'
  | 'wrong_length'
  | 'invalid_chars';

export type HexValidation =
  | { ok: true; value: string }
  | { ok: false; reason: HexValidationError };

export function validateHexColor(raw: string): HexValidation {
  if (raw.length === 0) return { ok: false, reason: 'empty' };
  if (!raw.startsWith('#')) return { ok: false, reason: 'missing_hash' };
  // 6 hex chars after the '#'.
  if (raw.length !== 7) return { ok: false, reason: 'wrong_length' };
  if (!HEX_COLOR_PATTERN.test(raw)) return { ok: false, reason: 'invalid_chars' };
  return { ok: true, value: raw.toLowerCase() };
}

export function describeHexError(reason: HexValidationError): string {
  switch (reason) {
    case 'empty':
      return 'Color is required.';
    case 'missing_hash':
      return 'Color must start with “#”.';
    case 'wrong_length':
      return 'Color must be 6 digits like #62A2C3.';
    case 'invalid_chars':
      return 'Color contains non-hex characters.';
  }
}

// ─── Logo slot map ───────────────────────────────────────────────────────────

export type BrandKitLogoSlot = 'primarySvg' | 'primaryPng' | 'favicon' | 'emailHeader';

/**
 * Logo-slot → (BrandAssetKind, expected MIME) map. Matches the canonical
 * `BrandAssetKind` enum in `apps/server/src/services/brand-asset-upload.ts`.
 * Keeping the mapping explicit here means the wizard never needs to know
 * the canonical filename strings — those are owned by the upload service.
 */
export const LOGO_SLOT_DEFS: ReadonlyArray<{
  slot: BrandKitLogoSlot;
  kind: BrandAssetKind;
  mimeType: string;
  label: string;
  hint: string;
  accept: string;
  required: boolean;
}> = [
  {
    slot: 'primarySvg',
    kind: 'logo-primary-svg',
    mimeType: 'image/svg+xml',
    label: 'Primary logo (SVG)',
    hint: 'Used in admin chrome and customer header. Must be parseable SVG.',
    accept: 'image/svg+xml,.svg',
    required: true,
  },
  {
    slot: 'favicon',
    kind: 'favicon-png',
    mimeType: 'image/png',
    label: 'Favicon (PNG)',
    hint: 'Browser tab icon. Must be 16×16 or 32×32 px.',
    accept: 'image/png,.png',
    required: true,
  },
  {
    slot: 'primaryPng',
    kind: 'logo-primary-png',
    mimeType: 'image/png',
    label: 'Primary logo (PNG)',
    hint: 'High-resolution raster fallback for emails and embeds where SVG is not supported. ≥ 1024 × 1024 px.',
    accept: 'image/png,.png',
    required: false,
  },
  {
    slot: 'emailHeader',
    kind: 'logo-email-header-png',
    mimeType: 'image/png',
    label: 'Email header (PNG)',
    hint: 'Top of transactional emails. Falls back to primary PNG when omitted. ≥ 1024 × 1024 px (we downscale).',
    accept: 'image/png,.png',
    required: false,
  },
];

// ─── Draft shape ─────────────────────────────────────────────────────────────

export type BrandKitLogoDraft = Partial<Record<BrandKitLogoSlot, string>>;

export type BrandKitColorsDraft = {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
  text?: string;
};

export type BrandKitTypographyDraft = {
  primaryFontFamily?: string;
  headingFontFamily?: string;
};

export type BrandKitCopyDraft = {
  companyName?: string;
  supportEmail?: string;
};

export type BrandKitDraft = {
  logo?: BrandKitLogoDraft;
  colors?: BrandKitColorsDraft;
  typography?: BrandKitTypographyDraft;
  copy?: BrandKitCopyDraft;
};

const COLOR_KEYS: ReadonlyArray<keyof BrandKitColorsDraft> = [
  'primary',
  'secondary',
  'accent',
  'background',
  'text',
];

const TYPOGRAPHY_KEYS: ReadonlyArray<keyof BrandKitTypographyDraft> = [
  'primaryFontFamily',
  'headingFontFamily',
];

const COPY_KEYS: ReadonlyArray<keyof BrandKitCopyDraft> = [
  'companyName',
  'supportEmail',
];

export const REQUIRED_COLOR_KEYS = COLOR_KEYS;
export const REQUIRED_TYPOGRAPHY_KEYS = TYPOGRAPHY_KEYS;
export const REQUIRED_COPY_KEYS = COPY_KEYS;
export const REQUIRED_LOGO_SLOTS: ReadonlyArray<BrandKitLogoSlot> = LOGO_SLOT_DEFS.filter(
  (d) => d.required,
).map((d) => d.slot);

export function readBrandKitDraft(payload: Record<string, unknown>): BrandKitDraft {
  const raw = payload.brandKit;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  return {
    logo: readObject(r.logo) as BrandKitLogoDraft | undefined,
    colors: readObject(r.colors) as BrandKitColorsDraft | undefined,
    typography: readObject(r.typography) as BrandKitTypographyDraft | undefined,
    copy: readObject(r.copy) as BrandKitCopyDraft | undefined,
  };
}

function readObject(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

export function setLogoUrl(
  current: BrandKitDraft,
  slot: BrandKitLogoSlot,
  url: string,
): BrandKitDraft {
  return {
    ...current,
    logo: { ...(current.logo ?? {}), [slot]: url },
  };
}

export function setColor(
  current: BrandKitDraft,
  key: keyof BrandKitColorsDraft,
  value: string,
): BrandKitDraft {
  return {
    ...current,
    colors: { ...(current.colors ?? {}), [key]: value },
  };
}

export function setTypography(
  current: BrandKitDraft,
  key: keyof BrandKitTypographyDraft,
  value: string,
): BrandKitDraft {
  return {
    ...current,
    typography: { ...(current.typography ?? {}), [key]: value },
  };
}

export function setCopy(
  current: BrandKitDraft,
  key: keyof BrandKitCopyDraft,
  value: string,
): BrandKitDraft {
  return {
    ...current,
    copy: { ...(current.copy ?? {}), [key]: value },
  };
}

/**
 * Default values for the color picker on first mount. Matching the brand
 * tokens already shipped in `apps/admin-app/src/index.css`, so a wizard
 * draft saved without edits still parses cleanly through `brandKitSchema`
 * at commit time. Admins overwrite per-tenant.
 */
export const DEFAULT_COLORS: Required<BrandKitColorsDraft> = {
  primary: '#b87333',
  secondary: '#2f3e46',
  accent: '#e8a849',
  background: '#fffaf3',
  text: '#2f3e46',
};

/**
 * UX-2 — completion accounting for the brand-kit step's progress bar.
 *
 * "Required" matches the schema gate at commit time:
 *   - 2 required logo slots (primarySvg + favicon per UX-3)
 *   - 5 colors (each must hex-validate)
 *   - 2 typography slots (non-empty trimmed string)
 *   - 2 copy fields (non-empty trimmed string)
 *
 * Returns counts plus a stable list of missing field keys so the UI
 * can render "8 of 11 complete · missing primarySvg, primary color".
 *
 * NOTE on color counting: a slot whose value FAILS hex validation is
 * counted as incomplete, even if the field is non-empty. Without this,
 * a typo'd `#62A2GZ` would falsely tick the bar to 100%. This matches
 * the schema enforcement at commit time — the user shouldn't be told
 * "complete" only to be 422'd on submit.
 */
export type BrandKitCompletion = {
  completed: number;
  total: number;
  missing: ReadonlyArray<string>;
};

export function computeBrandKitCompletion(draft: BrandKitDraft): BrandKitCompletion {
  const missing: string[] = [];
  let completed = 0;
  let total = 0;

  for (const slot of REQUIRED_LOGO_SLOTS) {
    total++;
    const url = draft.logo?.[slot];
    if (typeof url === 'string' && url.length > 0) {
      completed++;
    } else {
      missing.push(`logo.${slot}`);
    }
  }

  for (const key of REQUIRED_COLOR_KEYS) {
    total++;
    const raw = draft.colors?.[key];
    if (typeof raw === 'string' && validateHexColor(raw).ok) {
      completed++;
    } else {
      missing.push(`color.${key}`);
    }
  }

  for (const key of REQUIRED_TYPOGRAPHY_KEYS) {
    total++;
    const raw = draft.typography?.[key];
    if (typeof raw === 'string' && raw.trim().length > 0) {
      completed++;
    } else {
      missing.push(`typography.${key}`);
    }
  }

  for (const key of REQUIRED_COPY_KEYS) {
    total++;
    const raw = draft.copy?.[key];
    if (typeof raw === 'string' && raw.trim().length > 0) {
      completed++;
    } else {
      missing.push(`copy.${key}`);
    }
  }

  return { completed, total, missing };
}
